import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { useEffect } from "react";
import {
  Trophy, Users, ClipboardList, BarChart3, Megaphone, ScrollText, Zap,
  Share2, Shield, Timer, Camera, ChevronRight, CheckCircle2, MessageCircle, Mail, Phone, ArrowRight,
} from "lucide-react";

/** Public landing page (unauthenticated visitors).
    Uses the SUDEFOOTBALL brand palette (blue + white) which differs from the
    interior product palette (red + black).
    In production, when REACT_APP_LANDING_REDIRECT_URL is set (e.g. the GHL landing),
    unauthenticated visitors are redirected to that external URL. */
export default function LandingPage() {
  const { user } = useAuth();
  const redirectUrl = (process.env.REACT_APP_LANDING_REDIRECT_URL || "").trim();

  useEffect(() => {
    if (!user && redirectUrl) {
      window.location.replace(redirectUrl);
    }
  }, [user, redirectUrl]);

  if (user) {
    // Already logged in — go straight to the role home
    return <Navigate to={roleHome(user.role)} replace />;
  }
  if (redirectUrl) {
    // Show minimal splash while browser redirects to GHL
    return (
      <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center" data-testid="landing-redirect-splash">
        <div className="text-center">
          <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-blue-700">Sudefootball</div>
          <p className="mt-2 text-sm text-slate-600">Redirigiendo…</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-slate-50" aria-hidden />
        <div className="relative max-w-6xl mx-auto px-4 md:px-6 pt-12 md:pt-24 pb-16 md:pb-28">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-blue-700">Análisis · Estadísticas · Fútbol Base</div>
              <h1 className="mt-4 text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter uppercase leading-[0.95] text-slate-900">
                Cada <span className="text-blue-700">indicador</span>. Cada jornada. Cada jugador.
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-slate-600 max-w-lg">
                La única app de <b>análisis estadístico</b> diseñada para entrenadores y coordinadores de fútbol base. Plantillas de puntuación editables por categoría, bloques e indicadores a medida del club o del equipo específico, comparativa entre temporadas, mejores partidos automáticos y ficha pública del jugador para las familias. Convierte la intuición del cuerpo técnico en <b>decisiones respaldadas por datos</b>.
              </p>
              <div className="mt-8 flex items-center gap-3 flex-wrap">
                <a href="#contacto" data-testid="hero-demo-btn" className="inline-flex items-center gap-2 h-12 px-6 bg-blue-700 hover:bg-blue-800 text-white font-heading uppercase tracking-wider text-sm transition-colors">
                  Probar 15 días gratis <ArrowRight size={16} />
                </a>
                <Link to="/guia" data-testid="hero-guide-btn" className="inline-flex items-center gap-2 h-12 px-6 border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-heading uppercase tracking-wider text-sm transition-colors">
                  Ver cómo funciona
                </Link>
              </div>
              <div className="mt-8 flex items-center gap-6 text-xs text-slate-500 flex-wrap">
                <span className="flex items-center gap-1"><CheckCircle2 size={13} className="text-blue-700" /> Plantillas editables</span>
                <span className="flex items-center gap-1"><CheckCircle2 size={13} className="text-blue-700" /> Estadísticas por 90&#39;</span>
                <span className="flex items-center gap-1"><CheckCircle2 size={13} className="text-blue-700" /> Ficha pública para familias</span>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] bg-gradient-to-br from-blue-700 to-slate-900 border-4 border-slate-900 shadow-2xl relative overflow-hidden">
                <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.5) 0%, transparent 40%)" }} />
                <div className="absolute top-6 left-6 right-6">
                  <div className="text-[9px] uppercase tracking-[0.3em] text-blue-200 font-bold">Panel del entrenador · Live</div>
                </div>
                <div className="absolute inset-x-6 top-16 grid grid-cols-3 gap-3">
                  {[{label:"Goles",v:"127",c:"text-emerald-400"},{label:"Asist.",v:"84",c:"text-white"},{label:"Rating",v:"3.2",c:"text-white"}].map((k,i)=>(
                    <div key={i} className="border border-white/20 bg-white/5 p-3">
                      <div className="text-[9px] uppercase tracking-[0.15em] text-white/60">{k.label}</div>
                      <div className={`text-3xl font-black leading-none mt-1 ${k.c}`}>{k.v}</div>
                    </div>
                  ))}
                </div>
                <div className="absolute inset-x-6 bottom-6">
                  <div className="text-white font-black text-2xl uppercase tracking-tighter">CF ALEVÍN A</div>
                  <div className="text-blue-200 text-xs uppercase tracking-wider">Temporada 2025-26 · 12 jugadores</div>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white border-2 border-slate-900 shadow-xl p-4 hidden md:block">
                <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-blue-700">Convocatoria enviada</div>
                <div className="mt-1 text-sm font-bold text-slate-900">14 padres notificados por email</div>
                <div className="text-xs text-slate-500 mt-0.5">RSVP con un click</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="funcionalidades" className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-blue-700">Todo lo que necesitas</div>
            <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tight uppercase leading-none">Diseñado por y para el fútbol base</h2>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <div key={i} data-testid={`feature-${i}`} className="border border-slate-200 bg-white p-6 hover:border-blue-700 hover:shadow-lg transition-all group">
                <div className="w-12 h-12 border-2 border-blue-700 bg-blue-50 flex items-center justify-center text-blue-700 group-hover:bg-blue-700 group-hover:text-white transition-colors">
                  <f.icon size={20} />
                </div>
                <h3 className="mt-5 font-black uppercase tracking-tight text-lg text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="como-funciona" className="py-16 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-blue-700">Cómo funciona</div>
            <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tight uppercase leading-none">Del entrenamiento al análisis en 4 pasos</h2>
          </div>
          <div className="mt-14 grid md:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-black text-blue-100 leading-none">{String(i + 1).padStart(2, "0")}</div>
                <h4 className="mt-3 font-black uppercase tracking-tight text-slate-900">{s.title}</h4>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="planes" className="py-16 md:py-24 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-blue-300">Planes a medida</div>
            <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tight uppercase leading-none">Adaptable a cualquier club</h2>
            <p className="mt-4 text-slate-400 text-sm">Elige el plan que mejor encaja con el tamaño y ambición de tu club. Todos incluyen el núcleo completo de análisis.</p>
          </div>
          <div className="mt-8 max-w-3xl mx-auto border-2 border-amber-400 bg-amber-400/10 p-4 md:p-5 text-center" data-testid="promo-banner">
            <div className="text-[10px] uppercase tracking-[0.25em] font-black text-amber-300">Precio de Lanzamiento · Nuevos Clubes</div>
            <div className="mt-2 text-lg md:text-xl font-black text-amber-100">15 días de prueba gratis · Pago anual: 12 meses al precio de 11</div>
            <div className="mt-1 text-xs text-amber-100/70">Sin permanencia. Sin tarjeta durante la prueba. Te respetamos el precio durante 24 meses.</div>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {PLANS.map((p, i) => (
              <div key={i} data-testid={`plan-${p.key}`} className={`p-6 md:p-8 relative ${p.featured ? "bg-blue-700 border-2 border-blue-500" : "bg-slate-800 border border-slate-700"}`}>
                {p.featured && (
                  <div className="absolute -top-3 left-6 bg-blue-950 border border-blue-500 text-blue-300 text-[10px] uppercase tracking-widest font-bold px-3 py-1">Recomendado</div>
                )}
                <div className="text-[11px] uppercase tracking-widest font-bold opacity-70">{p.tagline}</div>
                <h3 className="mt-2 text-3xl font-black uppercase tracking-tight">{p.name}</h3>
                <div className="mt-4 text-sm opacity-80">{p.desc}</div>

                <div className="mt-6 border-t border-white/10 pt-5" data-testid={`plan-price-${p.key}`}>
                  <div className="flex items-baseline gap-2">
                    <span className={`text-4xl md:text-5xl font-black tracking-tight ${p.featured ? "text-white" : "text-blue-300"}`}>
                      {p.promo.toString().replace(".", ",")}€
                    </span>
                    <span className="text-xs opacity-70">/mes</span>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs">
                    <span className={`px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wider ${p.featured ? "bg-amber-400 text-black" : "bg-amber-400/20 text-amber-300"}`}>Precio de lanzamiento</span>
                  </div>
                  <div className={`mt-2 text-[10px] uppercase tracking-wider font-bold ${p.featured ? "text-blue-100" : "text-slate-400"}`}>Sin IVA · Precio respetado 24 meses</div>
                </div>

                <ul className="mt-6 space-y-2">
                  {p.features.map((ft, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 size={14} className={`mt-1 shrink-0 ${p.featured ? "text-blue-200" : "text-blue-400"}`} />
                      <span>{ft}</span>
                    </li>
                  ))}
                </ul>
                <a href="#contacto" data-testid={`plan-cta-${p.key}`} className={`mt-6 inline-flex items-center gap-2 h-11 px-5 w-full justify-center font-heading uppercase tracking-wider text-xs ${p.featured ? "bg-white text-blue-700 hover:bg-slate-100" : "bg-blue-700 text-white hover:bg-blue-600"} transition-colors`}>
                  Empezar <ChevronRight size={14} />
                </a>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-[11px] text-slate-500">Precios sin IVA. Después de los 12 primeros meses se aplica el precio estándar. Cambio de plan o baja en cualquier momento sin permanencia.</p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-blue-700">Lo que dicen los clubes</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-black tracking-tight uppercase leading-none">De Excel a decisiones basadas en datos</h2>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <blockquote key={i} className="border-l-4 border-blue-700 bg-slate-50 p-6">
                <p className="text-sm leading-relaxed text-slate-700 italic">&ldquo;{t.text}&rdquo;</p>
                <div className="mt-4">
                  <div className="text-sm font-bold text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.role}</div>
                </div>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {/* Familias (B2C) */}
      <section id="familias" className="py-16 md:py-24 bg-emerald-950 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="text-[10px] uppercase tracking-[0.25em] font-black text-emerald-400">Nuevo · Para madres y padres</div>
          <h2 className="mt-3 text-2xl md:text-4xl font-black">Sigue las estadísticas de tu hijo/a por 1€ al mes</h2>
          <p className="mt-3 text-sm md:text-base text-emerald-100/80 max-w-2xl mx-auto">
            ¿Tu club aún no usa Sudefootball? Da igual. Registra tú sus partidos desde el móvil en 1 minuto:
            goles, minutos, evolución, mejores partidos y su ficha para compartir con la familia. <b>Pago anual: 12€ por jugador (IVA incluido)</b>.
          </p>
          <FamiliaSignupForm />
          <div className="mt-4 text-[11px] text-emerald-100/60">Sin permanencia · Añade hasta 8 jugadores · Precio de lanzamiento respetado 24 meses</div>
        </div>
      </section>

      <section id="faq" className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center">
            <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-blue-700">Preguntas frecuentes</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-black tracking-tight uppercase leading-none">Lo que sueles preguntarte</h2>
          </div>
          <div className="mt-12 space-y-3">
            {FAQ.map((q, i) => (
              <details key={i} className="border border-slate-200 bg-white group" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer p-5 flex items-center justify-between font-bold text-slate-900 hover:text-blue-700 transition-colors">
                  <span>{q.q}</span>
                  <ChevronRight size={16} className="group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{q.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contacto" className="py-16 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-blue-700">Contacto</div>
              <h2 className="mt-3 text-3xl md:text-4xl font-black tracking-tight uppercase leading-none">Llevamos tu club al siguiente nivel</h2>
              <p className="mt-6 text-slate-600 leading-relaxed">
                Escríbenos y te enseñamos Sudefootball en 15 minutos. Sin compromiso. Sin instalación. Sólo un vistazo a cómo profesionalizar tu día a día.
              </p>
              <div className="mt-8 space-y-3">
                <a href="mailto:sudefootball@sudeporte.com" className="flex items-center gap-3 text-slate-900 hover:text-blue-700 transition-colors group" data-testid="contact-email">
                  <div className="w-11 h-11 border border-slate-300 group-hover:border-blue-700 flex items-center justify-center"><Mail size={16} /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Email</div>
                    <div className="font-bold">sudefootball@sudeporte.com</div>
                  </div>
                </a>
                <a href="tel:+34624037426" className="flex items-center gap-3 text-slate-900 hover:text-blue-700 transition-colors group" data-testid="contact-phone">
                  <div className="w-11 h-11 border border-slate-300 group-hover:border-blue-700 flex items-center justify-center"><Phone size={16} /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Teléfono</div>
                    <div className="font-bold">+34 624 037 426</div>
                  </div>
                </a>
                <a href="https://wa.me/34624037426?text=Hola,%20quiero%20conocer%20Sudefootball" target="_blank" rel="noreferrer" className="flex items-center gap-3 text-slate-900 hover:text-blue-700 transition-colors group" data-testid="contact-whatsapp">
                  <div className="w-11 h-11 border border-slate-300 group-hover:border-blue-700 flex items-center justify-center"><MessageCircle size={16} /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">WhatsApp</div>
                    <div className="font-bold">Escríbenos ahora</div>
                  </div>
                </a>
              </div>
            </div>
            <ContactForm />
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function ContactForm() {
  const submit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const subject = `Sudefootball · Demo para ${fd.get("club") || "mi club"}`;
    const body = `Hola,%0D%0A%0D%0AQuiero ver una demo de Sudefootball.%0D%0A%0D%0ANombre: ${fd.get("name")}%0D%0AClub: ${fd.get("club")}%0D%0AEmail: ${fd.get("email")}%0D%0ATeléfono: ${fd.get("phone") || "—"}%0D%0AMensaje:%0D%0A${fd.get("message") || "—"}`;
    window.location.href = `mailto:sudefootball@sudeporte.com?subject=${encodeURIComponent(subject)}&body=${body}`;
  };
  return (
    <form onSubmit={submit} className="border-2 border-slate-900 p-6 md:p-8 bg-slate-50 space-y-4" data-testid="contact-form">
      <div className="grid grid-cols-2 gap-3">
        <Input name="name" label="Nombre *" required />
        <Input name="club" label="Nombre del club *" required />
      </div>
      <Input type="email" name="email" label="Email *" required />
      <Input name="phone" label="Teléfono (opcional)" />
      <div>
        <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-700 mb-1">Mensaje</label>
        <textarea name="message" rows={3} className="w-full border border-slate-300 focus:border-blue-700 focus:outline-none px-3 py-2 text-sm bg-white" placeholder="Categorías, jugadores aprox., necesidades…" />
      </div>
      <button type="submit" data-testid="contact-submit-btn" className="w-full h-12 bg-blue-700 hover:bg-blue-800 text-white font-heading uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2 transition-colors">
        Enviar solicitud <ArrowRight size={15} />
      </button>
      <p className="text-[10px] text-slate-500 leading-relaxed">
        Al enviar aceptas nuestra <Link to="/privacidad" className="text-blue-700 hover:underline">política de privacidad</Link>. Sólo usamos tus datos para responder tu consulta.
      </p>
    </form>
  );
}

function Input({ label, ...props }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-700 mb-1">{label}</label>
      <input {...props} className="w-full h-11 border border-slate-300 focus:border-blue-700 focus:outline-none px-3 text-sm bg-white" />
    </div>
  );
}

export function SiteHeader() {
  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-6xl mx-auto flex items-center justify-between px-4 md:px-6 h-16">
        <Link to="/" className="flex items-center gap-3" data-testid="site-logo">
          <div className="flex flex-col items-start leading-none">
            <img src="/brand/sudefootball-40.png" alt="Sudefootball" className="h-7 md:h-8 w-auto object-contain" />
            <span className="font-heading font-black italic tracking-tight leading-none text-slate-900 text-xs md:text-sm mt-1">sudeporte</span>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm font-semibold text-slate-700">
          <a href="/#funcionalidades" className="hover:text-blue-700 transition-colors">Funcionalidades</a>
          <a href="/#como-funciona" className="hover:text-blue-700 transition-colors">Cómo funciona</a>
          <a href="/#planes" className="hover:text-blue-700 transition-colors">Planes</a>
          <a href="/#faq" className="hover:text-blue-700 transition-colors">FAQ</a>
          <Link to="/guia" className="hover:text-blue-700 transition-colors">Guía</Link>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="site-login-link" className="h-10 px-4 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white text-xs uppercase font-bold tracking-wider inline-flex items-center transition-colors">Acceder</Link>
          <a href="/#contacto" className="hidden sm:inline-flex h-10 px-4 bg-blue-700 hover:bg-blue-800 text-white text-xs uppercase font-bold tracking-wider items-center transition-colors">Demo</a>
        </div>
      </div>
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="bg-slate-950 text-slate-400 py-12">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex flex-col items-start leading-none">
              <img src="/brand/sudefootball-40.png" alt="Sudefootball" className="h-7 w-auto object-contain brightness-0 invert" />
              <span className="font-heading font-black italic tracking-tight leading-none text-white text-sm mt-2">sudeporte</span>
            </div>
            <p className="mt-4 text-xs leading-relaxed">Sudefootball · Análisis y estadísticas para clubes de fútbol.</p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white font-bold mb-3">Producto</div>
            <ul className="space-y-2 text-xs">
              <li><a href="/#funcionalidades" className="hover:text-white transition-colors">Funcionalidades</a></li>
              <li><a href="/#planes" className="hover:text-white transition-colors">Planes</a></li>
              <li><Link to="/guia" className="hover:text-white transition-colors">Guía</Link></li>
              <li><Link to="/login" className="hover:text-white transition-colors">Acceso clubes</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white font-bold mb-3">Legal</div>
            <ul className="space-y-2 text-xs">
              <li><Link to="/aviso-legal" className="hover:text-white transition-colors">Aviso legal</Link></li>
              <li><Link to="/privacidad" className="hover:text-white transition-colors">Privacidad</Link></li>
              <li><Link to="/cookies" className="hover:text-white transition-colors">Cookies</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white font-bold mb-3">Contacto</div>
            <ul className="space-y-2 text-xs">
              <li><a href="mailto:sudefootball@sudeporte.com" className="hover:text-white transition-colors">sudefootball@sudeporte.com</a></li>
              <li><a href="tel:+34624037426" className="hover:text-white transition-colors">+34 624 037 426</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-slate-800 text-[10px] uppercase tracking-widest text-slate-500 flex flex-wrap items-center justify-between gap-4">
          <div>© {new Date().getFullYear()} Sudefootball · by Sudeporte · Todos los derechos reservados</div>
          <div>Hecho con ⚽ en España</div>
        </div>
      </div>
    </footer>
  );
}

function roleHome(role) {
  switch (role) {
    case "super_admin": return "/platform";
    case "club_admin": return "/club";
    case "coordinator": return "/coordinator";
    case "coach": return "/coach";
    default: return "/login";
  }
}

const FEATURES = [
  { icon: Trophy, title: "Gestión estructurada", desc: "Categorías, equipos y plantillas jerárquicas. Roles Admin/Coordinador/Entrenador con permisos precisos." },
  { icon: ClipboardList, title: "Análisis del jugador", desc: "Plantillas de puntuación editables por categoría (U6 a U16 y Portero). Bloques e indicadores personalizables." },
  { icon: Zap, title: "Jornada en vivo con borradores", desc: "Registra goles, tarjetas, minutos y cambios EN DIRECTO desde el móvil durante el partido. Guarda borradores por tiempos (1T / 2T / Final) como el acta de un árbitro y cierra la jornada al terminar." },
  { icon: BarChart3, title: "Estadísticas avanzadas", desc: "Filtros por categoría, equipo, jugador y fechas. Exporta a Excel y descarga PDFs individuales." },
  { icon: Timer, title: "Asistencia mensual", desc: "Vista calendario para marcar asistencia a entrenamientos. Justificación objetiva de convocatorias." },
  { icon: Megaphone, title: "Portal familias", desc: "Convocatorias por email con RSVP en un click. Libreta digital de contactos y auto-convocatoria inteligente." },
  { icon: Share2, title: "Panel público del jugador", desc: "Genera enlaces únicos con foto y stats para que la familia comparta por WhatsApp. Sin datos sensibles." },
  { icon: ScrollText, title: "Trazabilidad total", desc: "Historial de cambios (quién editó qué y cuándo). Suspensión automática por impago con avisos previos." },
  { icon: Shield, title: "Datos aislados", desc: "Multi-tenant: cada club tiene su espacio privado. Nadie ve lo que no le corresponde." },
];

const STEPS = [
  { title: "Alta del club", desc: "Sudefootball da de alta tu club en menos de 24h y te envía las credenciales por email." },
  { title: "Configura", desc: "Crea tus categorías, equipos y plantillas. Invita a coordinadores y entrenadores." },
  { title: "Registra", desc: "Los entrenadores registran jornadas, valoran a cada jugador y controlan asistencia." },
  { title: "Analiza", desc: "Consulta estadísticas por jugador/equipo/categoría. Exporta PDF y comparte con las familias." },
];

const PLANS = [
  { key: "presencia", tagline: "Empieza a ordenar", name: "Presencia", desc: "Ideal para clubes que empiezan a digitalizarse.", promo: 9.75, features: ["Hasta 5 equipos", "Registro de jornadas", "Asistencia mensual", "Estadísticas básicas", "Soporte por email"] },
  { key: "club", featured: true, tagline: "Nuestro más popular", name: "Club", desc: "El paquete estándar para la mayoría de clubes federados.", promo: 20.00, features: ["Hasta 30 equipos", "Todas las plantillas de análisis", "Portal de comunicación con familias", "Panel público del jugador", "Jornada en vivo con borradores", "Exportación Excel + PDF", "Soporte prioritario"] },
  { key: "total", tagline: "Sin límites", name: "Total", desc: "Para clubes grandes con multi-coordinador y plantillas personalizadas.", promo: 29.98, features: ["Todo lo del plan Club", "Plantillas personalizadas por equipo", "Coordinadores ilimitados", "Historial auditable completo", "Envío masivo a padres", "Onboarding personalizado", "SLA prioritario"] },
];

const TESTIMONIALS = [
  { text: "Antes gastábamos horas cada semana en Excel. Con Sudefootball el entrenador registra el partido en 5 minutos y las familias reciben las convocatorias automáticamente.", name: "Coordinador · Cantera", role: "Club Federado, Valencia" },
  { text: "Los padres agradecen ver el progreso de sus hijos con datos objetivos, no con opiniones. El enlace público a la ficha es un acierto.", name: "Presidente", role: "Club Base, Alicante" },
  { text: "La jornada en vivo con borradores cambió mi día a día. Marco goles y tarjetas desde el móvil durante el partido. Al llegar a casa ya tengo el acta lista.", name: "Entrenador U12", role: "Escuela Deportiva Municipal" },
];

const FAQ = [
  { q: "¿Necesitamos instalar algo?", a: "No. Sudefootball es 100% web. Se abre desde el navegador del móvil, tablet o PC. Además funciona como app instalable (PWA): puedes anclarla en la pantalla de inicio del móvil." },
  { q: "¿Cómo se protegen los datos de mi club?", a: "Cada club tiene su propio espacio aislado. Ningún club ve datos de otro. Los datos personales de padres nunca se muestran en los enlaces públicos." },
  { q: "¿Cuánto se tarda en dar de alta un club?", a: "Menos de 24h. Nos envías el email del administrador, categorías estimadas y el plan; te enviamos las credenciales listas para empezar." },
  { q: "¿Funciona sin internet?", a: "La consulta funciona parcialmente sin conexión (PWA cachea la interfaz). Para guardar cambios se requiere conexión. El registro en directo de la jornada es muy ligero, funciona con datos móviles sin problema." },
  { q: "¿Puedo cambiar de plan más adelante?", a: "Sí. Puedes cambiar de plan en cualquier momento sin perder ningún dato. Contáctanos y lo ajustamos en el mismo día." },
  { q: "¿Ofrecéis formación al equipo técnico?", a: "Sí. El plan Total incluye onboarding personalizado. En cualquier plan tienes acceso a la Guía completa y a soporte por email/WhatsApp." },
];

export function FamiliaSignupForm() {
  const [form, setForm] = React.useState({ parent_name: "", email: "", phone: "", children_count: 1 });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      // UTM de anuncios (Meta/GHL): se leen de la URL o de la sesión y viajan con el lead
      let utm = {};
      try {
        const params = new URLSearchParams(window.location.search);
        for (const [k, v] of params) if (k.startsWith("utm_")) utm[k] = v;
        if (Object.keys(utm).length) sessionStorage.setItem("familia_utm", JSON.stringify(utm));
        else utm = JSON.parse(sessionStorage.getItem("familia_utm") || "{}");
      } catch { /* ignore */ }
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/familias/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, utm }),
      });
      const data = await resp.json();
      if (!resp.ok) { setError(data.detail || "No se pudo iniciar el pago"); return; }
      try {
        const { trackPixel } = await import("@/lib/metaPixel");
        trackPixel("InitiateCheckout", { value: 12 * Number(form.children_count || 1), currency: "EUR", content_name: "sudefootball_player" });
      } catch { /* pixel opcional */ }
      window.location.href = data.checkout_url;
    } catch { setError("Error de red — inténtalo de nuevo"); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-3 justify-center items-stretch max-w-2xl mx-auto" data-testid="familia-signup-form">
      <input required data-testid="familia-name" placeholder="Tu nombre" value={form.parent_name}
             onChange={(e) => setForm(f => ({ ...f, parent_name: e.target.value }))}
             className="h-12 px-4 bg-card border border-border text-foreground placeholder:text-muted-foreground text-sm focus:border-primary focus:outline-none flex-1" />
      <input required type="email" data-testid="familia-email" placeholder="Tu email" value={form.email}
             onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
             className="h-12 px-4 bg-card border border-border text-foreground placeholder:text-muted-foreground text-sm focus:border-primary focus:outline-none flex-1" />
      <input required type="tel" inputMode="tel" pattern="[+]?[0-9\s-]{9,}" data-testid="familia-phone"
             placeholder="Tu móvil (WhatsApp)" title="Mínimo 9 dígitos" value={form.phone}
             onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
             className="h-12 px-4 bg-card border border-border text-foreground placeholder:text-muted-foreground text-sm focus:border-primary focus:outline-none flex-1" />
      <select data-testid="familia-children" value={form.children_count}
              onChange={(e) => setForm(f => ({ ...f, children_count: Number(e.target.value) }))}
              className="h-12 px-3 bg-card border border-border text-foreground text-sm focus:border-primary focus:outline-none">
        {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n} jugador{n > 1 ? "es" : ""} · {n * 12}€/año</option>)}
      </select>
      <button type="submit" disabled={loading} data-testid="familia-submit"
              className="h-12 px-6 bg-primary text-white font-heading font-black uppercase tracking-wider text-sm hover:bg-primary/90 disabled:opacity-60">
        {loading ? "Un momento…" : "Empezar"}
      </button>
      {error && <div className="text-primary text-xs w-full sm:basis-full sm:order-last text-center" data-testid="familia-error">{error}</div>}
    </form>
  );
}
